import React, { memo, useCallback, useEffect, useRef } from 'react'
import { Alert, Form, Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import {
  getMontageFormData,
  getMontageFormLoading,
  getVisibleFormData,
  getMontageFormError,
} from '../../selectors'
import { useDebounce } from 'src/react-app/hooks/useDebounce/useDebounce'
import { HStack, VStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { useNavigate } from 'react-router-dom'

interface MontageFormsProps {
  className?: string
  id: string | number
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

const MontageForms: React.FC<MontageFormsProps> = memo((props: MontageFormsProps) => {
  const { id } = props
  const [form] = Form.useForm()
  const montageFormData = useSelector(getMontageFormData)
  const montageFormIsLoading = useSelector(getMontageFormLoading)
  const montageFormError = useSelector(getMontageFormError)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()
  const isInitialRender = useRef(true)

  const saveCase = useCallback(
    async (is_next_stage: boolean, is_done: boolean | undefined = undefined) => {
      await saveResult(
        id,
        visibleFormData,
        montageFormData?.stages?.current,
        is_next_stage,
        is_done,
      )
    },
    [id, visibleFormData],
  )

  const debouncedSaveCase = useDebounce(saveCase, 1000)

  useEffect(() => {
    const redirectUrl = montageFormData?.redirectUrl
    if (redirectUrl) {
      navigate(redirectUrl)
    }
  }, [montageFormData])

  useEffect(() => {
    if (visibleFormData) {
      isInitialRender.current = false
    }
  }, [visibleFormData])

  const onChangeForm = useCallback(
    async ({ formData }) => {
      if (isInitialRender.current) {
        return
      }
      if (!visibleFormData) {
        return
      }
      const isFilesEdit = JSON.stringify(formData) === JSON.stringify(visibleFormData?.resultObject)
      if (isFilesEdit) {
        return
      }
      visibleFormData.resultObject = { ...formData }
      debouncedSaveCase(false)
    },
    [visibleFormData, debouncedSaveCase],
  )

  if (montageFormError && !montageFormIsLoading && !montageFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка. Попробуйте перезагрузить страницу или открыть
        карточку повторно.
      </HStack>
    )
  }

  return (
    <>
      <HStack gap={'8'} max>
        <Form
          {...formItemLayout}
          layout="horizontal"
          form={form}
          scrollToFirstError={true}
          style={{ width: '100%' }}
        >
          {montageFormError && !montageFormIsLoading ? (
            <div className={'alertWrapper'}>
              <Alert
                message="При выполнении операции возникла ошибка:"
                showIcon
                type="error"
                description={montageFormError}
              />
            </div>
          ) : null}
          {!montageFormIsLoading ? (
            <VStack gap={'16'} max align={'stretch'} className={'appealsForm'}>
              {visibleFormData && (
                <DefaultJsonForm
                  formData={{ ...visibleFormData.resultObject }}
                  schema={visibleFormData.resultSchema}
                  onChange={onChangeForm}
                  orientation="horizontal"
                />
              )}
            </VStack>
          ) : (
            <Skeleton active />
          )}
        </Form>
      </HStack>
    </>
  )
})

MontageForms.displayName = 'MontageForms'

export default MontageForms
